import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  User, 
  Save, 
  LogOut, 
  Trash2, 
  Settings, 
  ArrowLeft, 
  Sparkles,
  Bell,
  Shield,
  Key,
  CreditCard,
  Activity,
  Mail
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ThemeToggle } from "@/components/ThemeToggle";

interface UserProfile {
  id: string;
  email: string;
  created_at: string;
  display_name: string | null;
  bio: string | null;
  preferred_prompt_type: "Image" | "Text" | "Video" | "App" | "All" | null;
  avatar_url: string | null;
  last_login: string;
  total_prompts: number;
  plan: "Free" | "Pro";
}

const Profile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    display_name: "",
    bio: "",
    preferred_prompt_type: "",
  });

  useEffect(() => {
    checkAuth();
    loadProfile();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/login");
      return;
    }

    if (!session.user.email_confirmed_at) {
      toast({
        title: "Email not verified",
        description: "Please verify your email address before accessing your profile.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
  };

  const loadProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (error) throw error;

      const { count } = await supabase
        .from("prompts")
        .select("*", { count: "exact", head: true })
        .eq("user_id", session.user.id);

      const userProfile: UserProfile = {
        id: session.user.id,
        email: session.user.email!,
        created_at: session.user.created_at,
        display_name: profile?.display_name || null,
        bio: profile?.bio || null,
        preferred_prompt_type: profile?.preferred_prompt_type || null,
        avatar_url: session.user.user_metadata?.avatar_url || null,
        last_login: profile?.last_login || session.user.last_sign_in_at,
        total_prompts: count || 0,
        plan: profile?.plan || "Free",
      };

      setProfile(userProfile);
      setFormData({
        display_name: userProfile.display_name || "",
        bio: userProfile.bio || "",
        preferred_prompt_type: userProfile.preferred_prompt_type || "",
      });
    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile data.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    setIsSaving(true);

    try {
      const preferred_prompt_type = formData.preferred_prompt_type || null;
      if (preferred_prompt_type && !["Image", "Text", "Video", "App", "All"].includes(preferred_prompt_type)) {
        throw new Error("Invalid prompt type selected");
      }

      const updateData = {
        id: profile.id,
        display_name: formData.display_name.trim() || null,
        bio: formData.bio.trim() || null,
        preferred_prompt_type,
        plan: profile.plan,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully.",
      });
      
      await loadProfile();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error instanceof Error 
          ? `Failed to update profile: ${error.message}`
          : "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
  };

  const handleDeleteAccount = async () => {
    try {
      toast({
        title: "Deleting account",
        description: "Please wait while we delete your account...",
      });

      const { error: promptsError } = await supabase
        .from("prompts")
        .delete()
        .eq("user_id", profile?.id);

      if (promptsError) throw promptsError;

      const { error: profileError } = await supabase
        .from("profiles")
        .delete()
        .eq("id", profile?.id);

      if (profileError) throw profileError;

      const { error: authError } = await supabase.auth.admin.deleteUser(
        profile?.id as string
      );

      if (authError) {
        console.error("Error deleting auth user:", authError);
        await supabase.auth.signOut();
      }

      navigate("/");
      toast({
        title: "Account deleted",
        description: "Your account has been successfully deleted. We're sorry to see you go!",
      });
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Error",
        description: "Failed to delete account. Please try again or contact support.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Profile Settings" 
        showBack={true}
        backPath="/dashboard"
      />

      <div className="container max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Profile Overview Card - Spans full width */}
          <Card className="col-span-12 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative group">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={profile.avatar_url || undefined} />
                    <AvatarFallback>
                      <User className="h-12 w-12" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="ghost" size="sm" className="text-white">
                      Change
                    </Button>
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-2xl font-bold text-foreground">
                    {profile.display_name || "Set your display name"}
                  </h2>
                  <p className="text-muted-foreground">{profile.email}</p>
                  <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
                    <Badge variant="secondary">
                      {profile.plan} Plan
                    </Badge>
                    <Badge variant="outline">
                      {profile.total_prompts} Prompts
                    </Badge>
                    <Badge variant="outline">
                      Member since {new Date(profile.created_at).toLocaleDateString()}
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <ThemeToggle />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="gap-2">
                        <LogOut className="h-4 w-4" />
                        Logout
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You will need to sign in again to access your account.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleLogout}>
                          Logout
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content Area */}
          <div className="col-span-12">
            <Tabs defaultValue="general" className="space-y-6">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <TabsTrigger value="general" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  General
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Notifications
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Security
                </TabsTrigger>
                <TabsTrigger value="billing" className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Billing
                </TabsTrigger>
              </TabsList>

              {/* General Settings Tab */}
              <TabsContent value="general" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                    <CardDescription>
                      Update your profile information and preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="display_name">Display Name</Label>
                        <Input
                          id="display_name"
                          value={formData.display_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              display_name: e.target.value,
                            }))
                          }
                          placeholder="Enter your display name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea
                          id="bio"
                          value={formData.bio}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              bio: e.target.value,
                            }))
                          }
                          placeholder="Tell us about yourself"
                          className="min-h-[100px]"
                        />
                        <p className="text-xs text-muted-foreground">
                          {formData.bio?.length || 0}/160 characters
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="prompt_type">Preferred Prompt Type</Label>
                        <Select
                          value={formData.preferred_prompt_type}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              preferred_prompt_type: value,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select preferred type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Image">Image</SelectItem>
                            <SelectItem value="Text">Text</SelectItem>
                            <SelectItem value="Video">Video</SelectItem>
                            <SelectItem value="App">App</SelectItem>
                            <SelectItem value="All">All</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end gap-4">
                  <Button
                    variant="default"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full md:w-auto"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>

              {/* Notifications Tab */}
              <TabsContent value="notifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Notification Preferences</CardTitle>
                    <CardDescription>
                      Manage how you receive notifications
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>Email Notifications</Label>
                          <p className="text-sm text-muted-foreground">
                            Receive notifications about your account via email
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>Product Updates</Label>
                          <p className="text-sm text-muted-foreground">
                            Get notified about new features and improvements
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>Security Alerts</Label>
                          <p className="text-sm text-muted-foreground">
                            Receive alerts about your account security
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                    <CardDescription>
                      Manage your account security and privacy
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">Change Password</h3>
                          <p className="text-sm text-muted-foreground">
                            Update your password regularly to keep your account secure
                          </p>
                        </div>
                        <Button variant="outline">
                          <Key className="w-4 h-4 mr-2" />
                          Change
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">Two-Factor Authentication</h3>
                          <p className="text-sm text-muted-foreground">
                            Add an extra layer of security to your account
                          </p>
                        </div>
                        <Button variant="outline">
                          <Shield className="w-4 h-4 mr-2" />
                          Enable
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">Active Sessions</h3>
                          <p className="text-sm text-muted-foreground">
                            Manage devices where you're currently signed in
                          </p>
                        </div>
                        <Button variant="outline">
                          <Activity className="w-4 h-4 mr-2" />
                          Manage
                        </Button>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" className="w-full md:w-auto">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-destructive">Delete Account</AlertDialogTitle>
                            <AlertDialogDescription className="space-y-2">
                              <p>Are you absolutely sure you want to delete your account? This action cannot be undone.</p>
                              <p>This will:</p>
                              <ul className="list-disc list-inside space-y-1">
                                <li>Delete all your prompts and templates</li>
                                <li>Remove your profile information</li>
                                <li>Cancel any active subscriptions</li>
                                <li>Permanently delete your account</li>
                              </ul>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDeleteAccount}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete Account
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Plan</CardTitle>
                    <CardDescription>
                      Manage your subscription and billing details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-medium">Current Plan</h3>
                          <p className="text-sm text-muted-foreground">
                            {profile.plan === "Pro" ? "Pro Plan - $19/month" : "Free Plan"}
                          </p>
                        </div>
                        <Badge variant={profile.plan === "Pro" ? "default" : "secondary"}>
                          {profile.plan}
                        </Badge>
                      </div>
                      {profile.plan === "Free" ? (
                        <Button className="w-full md:w-auto">
                          <Sparkles className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      ) : (
                        <Button variant="outline" className="w-full md:w-auto">
                          Manage Subscription
                        </Button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-medium">Usage Statistics</h3>
                      <div className="grid gap-4 md:grid-cols-3">
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-2xl font-bold">{profile.total_prompts}</div>
                            <p className="text-xs text-muted-foreground">Total Prompts</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-2xl font-bold">
                              {profile.plan === "Pro" ? "Unlimited" : "50"}
                            </div>
                            <p className="text-xs text-muted-foreground">Monthly Limit</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="text-2xl font-bold">
                              {new Date(profile.created_at).toLocaleDateString()}
                            </div>
                            <p className="text-xs text-muted-foreground">Member Since</p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 